import type { LiveSessionInsightDto } from '@/api/admin'

const signed = (n: number, unit = '') => `${n >= 0 ? '+' : ''}${n}${unit}`

/** Feedback is weighted by how many people answered; gain and confidence by how many took part. */
function weighted(items: { value: number | null; weight: number }[]): number | null {
  const used = items.filter((i): i is { value: number; weight: number } => i.value !== null && i.weight > 0)
  const total = used.reduce((s, i) => s + i.weight, 0)
  return total === 0 ? null : Math.round((used.reduce((s, i) => s + i.value * i.weight, 0) / total) * 10) / 10
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'good' | 'bad' }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm text-center">
      <p className={`text-3xl font-bold ${tone === 'good' ? 'text-green-600' : tone === 'bad' ? 'text-red-600' : 'text-gray-900'}`}>{value}</p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  )
}

export function LiveSummaryStats({ sessions }: { sessions: LiveSessionInsightDto[] }) {
  if (sessions.length === 0) return null
  const feedback = weighted(sessions.map(s => ({ value: s.feedbackAverage, weight: s.feedbackCount })))
  const gain = weighted(sessions.map(s => ({ value: s.learningGain, weight: s.participants })))
  const confidence = weighted(sessions.map(s => ({ value: s.confidenceChange, weight: s.participants })))
  const responses = sessions.reduce((n, s) => n + s.feedbackCount, 0)

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      <Stat label="Live sessions" value={String(sessions.length)} sub={`${sessions.reduce((n, s) => n + s.participants, 0)} participants`} />
      <Stat label="Attendee feedback" value={feedback !== null ? `${feedback}/10` : '—'} sub={`${responses} response${responses === 1 ? '' : 's'}`} />
      <Stat label="Learning gain" value={gain !== null ? signed(gain, ' pts') : '—'} tone={gain === null ? undefined : gain > 0 ? 'good' : gain < 0 ? 'bad' : undefined} />
      <Stat label="Confidence change" value={confidence !== null ? signed(confidence) : '—'} sub={confidence !== null ? 'out of 10' : undefined} tone={confidence === null ? undefined : confidence > 0 ? 'good' : confidence < 0 ? 'bad' : undefined} />
    </div>
  )
}

function Chip({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'good' | 'bad' | 'thin' }) {
  const colour = tone === 'good' ? 'border-green-200 bg-green-50 text-green-700'
    : tone === 'bad' ? 'border-red-200 bg-red-50 text-red-700'
    : tone === 'thin' ? 'border-amber-200 bg-amber-50 text-amber-700'
    : 'border-gray-200 bg-gray-50 text-gray-900'
  return (
    <div className={`rounded-lg border px-3 py-1.5 ${colour}`}>
      <p className="text-[11px] uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-sm font-semibold">{value}</p>
      {sub && <p className="text-[11px] opacity-70">{sub}</p>}
    </div>
  )
}

function SessionCard({ s, formatDate, showClient }: { s: LiveSessionInsightDto; formatDate: (d: string) => string; showClient: boolean }) {
  const commentCount = s.comments.reduce((n, g) => n + g.comments.length, 0)
  const tone = (n: number | null) => (n === null ? undefined : n > 0 ? 'good' as const : n < 0 ? 'bad' as const : undefined)

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm space-y-3">
      <div>
        <p className="font-semibold text-gray-900">{s.courseName}</p>
        <p className="text-sm text-gray-500">
          {showClient && s.clientName ? `${s.clientName} · ` : ''}{formatDate(s.date)}
          {s.trainers.length > 0 && ` · ${s.trainers.join(', ')}`}
          {` · ${s.participants} participant${s.participants === 1 ? '' : 's'}`}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {s.feedbackAverage !== null && (
          <Chip label="Feedback" value={`${s.feedbackAverage}/10`}
            sub={`${s.feedbackCount} response${s.feedbackCount === 1 ? '' : 's'}${s.smallSample ? ' · small sample' : ''}`}
            tone={s.smallSample ? 'thin' : undefined} />
        )}
        {s.learningGain !== null && (
          <Chip label="Learning gain" value={signed(s.learningGain, ' pts')}
            sub={s.preAverage !== null && s.postAverage !== null ? `${s.preAverage}% → ${s.postAverage}% (all)` : undefined} tone={tone(s.learningGain)} />
        )}
        {s.confidenceChange !== null && (
          <Chip label="Confidence" value={signed(s.confidenceChange)} sub={`${s.confidenceBefore ?? '–'} → ${s.confidenceAfter ?? '–'} of 10`} tone={tone(s.confidenceChange)} />
        )}
        {s.feedbackAverage === null && s.learningGain === null && s.confidenceChange === null && (
          <span className="text-xs text-gray-400">No scored results for this session.</span>
        )}
      </div>

      {commentCount > 0 && (
        <details>
          <summary className="cursor-pointer text-sm text-indigo-600 hover:text-indigo-800">
            {commentCount} comment{commentCount === 1 ? '' : 's'}
          </summary>
          <div className="mt-2 space-y-3">
            {s.comments.map(g => (
              <div key={g.question}>
                <p className="text-xs font-semibold text-gray-500">{g.question}</p>
                <ul className="mt-1 space-y-1">
                  {g.comments.map((c, i) => (
                    <li key={i} className="text-sm text-gray-600 bg-gray-50 border border-gray-100 rounded-lg px-3 py-1.5 whitespace-pre-wrap">{c}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  )
}

/** Ended CertSlate Live sessions: how attendees rated them, what they learned, what they wrote. Anonymous throughout. */
export function LiveSessionsList({
  sessions, formatDate, showClient = true,
}: {
  sessions: LiveSessionInsightDto[]
  formatDate: (isoDate: string) => string
  showClient?: boolean
}) {
  return (
    <div className="space-y-3">
      {sessions.map(s => <SessionCard key={s.deliveryId} s={s} formatDate={formatDate} showClient={showClient} />)}
    </div>
  )
}
